import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  Clock,
  ClipboardList,
  RefreshCw,
  XCircle,
  X,
  Calendar,
  User,
  FileText,
  AlertTriangle,
} from "lucide-react";
import {
  approveLeaveRequest,
  fetchActionableLeaveCount,
  fetchLeaveRequests,
  fetchMyLeaveQueue,
} from "../../../Moduels/leave/leaveApi";
import { notifyLeaveQueueUpdated } from "../../../Moduels/leave/leaveQueueEvents";
import EmployeeSelect from "../../common/EmployeeSelect";
import { findEmployeeById, formatEmployeeLabel } from "../../common/employeeUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { isAdminLike, normalizeRole } from "../../../rbac/access";
import {
  HrAlert,
  HrBtn,
  HrEmptyState,
  HrSelect,
} from "../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import ConfirmDialog from "../../ui/ConfirmDialog";
import GuidedNotice from "../../ui/GuidedNotice";
import { formatIndianDate } from "../../../Moduels/attendance/indianCalendarUtils";
import { useLeaveOrg } from "./useLeaveOrg";
import { LeaveKpi } from "./leaveTheme";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  escalated: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value) {
  return typeof value === "string" && UUID_RE.test(value.trim());
}

/** Non-empty when the pending step belongs to a configured organisation role (value is its name). */
function pendingOrgRole(req) {
  const r = req.pending_approver_role;
  return r && r !== "manager" && r !== "hr" ? r : null;
}

function statusLabel(req) {
  const orgRole = pendingOrgRole(req);
  if (orgRole && (req.status === "pending" || req.status === "escalated")) return `Awaiting ${orgRole}`;
  if (req.status === "escalated" || req.pending_approver_role === "hr") return "Awaiting HR";
  if (req.status === "pending" && req.pending_approver_role === "manager") return "Awaiting manager";
  if (req.status === "pending") return "Pending";
  return req.status;
}

function statusHint(req) {
  const orgRole = pendingOrgRole(req);
  if (req.can_act && orgRole) {
    return req.status === "escalated"
      ? `Level 1 approved. Your approval as ${orgRole} will finalize this request.`
      : `This request is assigned to the ${orgRole} role for approval.`;
  }
  if (req.can_act && req.pending_approver_role === "hr") {
    return "Manager approved. Your approval will finalize this request and sync to attendance.";
  }
  if (req.can_act && req.pending_approver_role === "manager") {
    return "This request is assigned to you for manager approval.";
  }
  if (orgRole) return `Awaiting ${orgRole} approval.`;
  if (req.pending_approver_role === "hr") return "Awaiting HR approval.";
  if (req.pending_approver_role === "manager") return "Awaiting reporting manager approval.";
  return null;
}

function approveButtonLabel(req, role) {
  if (
    isAdminLike(role) &&
    req.can_act &&
    (req.is_admin_override || req.pending_approver_role === "hr" || req.status === "escalated")
  ) {
    return "Finalize approval";
  }
  return "Approve";
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* ============================================================
   KPI CARD
============================================================ */

/* Attendance-style KPI; tone picked from the legacy colour prop. */
const KPI_TONE_BY_COLOR = { "#f59e0b": "amber", "#10b981": "green", "#ef4444": "red", "#2563eb": "indigo" };

function KpiCard({ label, value, color, suffix }) {
  return <LeaveKpi label={label} value={value} suffix={suffix} tone={KPI_TONE_BY_COLOR[String(color).toLowerCase()] || "indigo"} />;
}

/* ============================================================
   APPROVAL DRAWER
============================================================ */

function ApprovalDrawer({ req, employees, role, acting, onApprove, onReject, onClose }) {
  if (!req) return null;

  const employee = findEmployeeById(employees, req.employee_id);
  const empLabel = formatEmployeeLabel(employee) || req.employee_name || req.employee_code || "Employee";
  const normalizedRole = normalizeRole(role);

  const drawer = (
    <div
      className="fixed inset-0 z-[9998] flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />
      <div
        className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-[slideIn_200ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
        style={{ animationName: "slideIn" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Leave Request</p>
            <h3 className="text-[16px] font-bold text-slate-900">{req.leave_type_name || req.leave_type_code}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Status */}
          <div className="flex items-center gap-2">
            <span className={`text-[12px] px-2.5 py-1 rounded-full font-bold border ${STATUS_STYLES[req.status] || "bg-slate-100"}`}>
              {statusLabel(req)}
            </span>
            <span className="text-[22px] font-extrabold text-blue-600 tabular-nums ml-auto">
              {req.total_days}<span className="text-[13px] font-bold text-slate-400 ml-1">day{req.total_days !== 1 ? "s" : ""}</span>
            </span>
          </div>

          {statusHint(req) && (
            <div className={`text-[12px] px-3 py-2 rounded-lg border ${req.can_act ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
              {statusHint(req)}
            </div>
          )}

          {/* Employee */}
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
              <User size={11} /> Employee
            </p>
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[12px] font-bold shadow-sm">
                {empLabel.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-[13px] font-bold text-slate-900">{empLabel}</p>
                <p className="text-[11px] text-slate-500">
                  {employee?.department?.name || employee?.department_name || req.department_name || "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
              <Calendar size={11} /> Period
            </p>
            <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
              <p className="text-[13px] font-semibold text-slate-800">
                {formatIndianDate(req.start_date)} → {formatIndianDate(req.end_date)}
              </p>
              {req.half_day && (
                <span className="inline-block mt-1 text-[11px] bg-sky-100 text-sky-800 px-2 py-0.5 rounded-full font-bold">
                  Half-day
                </span>
              )}
            </div>
          </div>

          {/* Reason */}
          {req.reason && (
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
                <FileText size={11} /> Reason
              </p>
              <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
                <p className="text-[13px] text-slate-700 italic leading-relaxed">"{req.reason}"</p>
              </div>
            </div>
          )}

          {/* Applied date */}
          {req.created_at && (
            <div className="text-[11px] text-slate-400">
              Applied: {formatIndianDate(req.created_at?.split("T")[0] || req.created_at)}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {req.can_act && ["pending", "escalated"].includes(req.status) && (
          <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex gap-2">
            <button
              type="button"
              disabled={acting}
              onClick={() => onApprove(req)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm disabled:opacity-50 transition"
            >
              <CheckCircle2 size={15} />
              {approveButtonLabel(req, normalizedRole)}
            </button>
            <button
              type="button"
              disabled={acting}
              onClick={() => onReject(req)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 disabled:opacity-50 transition"
            >
              <XCircle size={15} />
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(drawer, document.body) : null;
}

/* ============================================================
   MAIN
============================================================ */

export default function LvLeaveApproval() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const { role } = useContext(AuthContext);
  const { employees } = useEmployees();
  const [filterEmp, setFilterEmp] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [requests, setRequests] = useState([]);
  const [serverQueueCount, setServerQueueCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actingId, setActingId] = useState(null);

  const [drawerReq, setDrawerReq] = useState(null);
  const [confirmReq, setConfirmReq] = useState(null);

  const normalizedRole = normalizeRole(role);
  const queueOnly = !statusFilter;
  const fetchIdRef = useRef(0);
  const skipNextAutoLoadRef = useRef(false);

  const employeeFilterId = isValidUuid(filterEmp) ? filterEmp.trim() : undefined;

  const clearBrowseFilters = useCallback(() => {
    setFilterEmp("");
    setStatusFilter("");
  }, []);

  useEffect(() => {
    if (location.state?.resetQueueFilters) {
      clearBrowseFilters();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, clearBrowseFilters]);

  const fetchQueueData = useCallback(async () => {
    if (!orgId) return null;
    if (queueOnly) {
      return fetchMyLeaveQueue(orgId);
    }
    return fetchLeaveRequests({
      organisation_id: orgId,
      employee_id: employeeFilterId,
      status: statusFilter || undefined,
      actionable_only: false,
    });
  }, [orgId, employeeFilterId, statusFilter, queueOnly]);

  const syncServerQueueCount = useCallback(async () => {
    if (!orgId || !queueOnly) {
      setServerQueueCount(null);
      return;
    }
    try {
      const count = await fetchActionableLeaveCount(orgId);
      setServerQueueCount(count);
    } catch {
      setServerQueueCount(null);
    }
  }, [orgId, queueOnly]);

  const applyQueueResult = useCallback((fetchId, data, { keepOnEmpty = false } = {}) => {
    if (fetchId !== fetchIdRef.current) return;
    if (!Array.isArray(data)) return;
    if (data.length > 0) {
      setRequests(data);
      return;
    }
    if (!keepOnEmpty) {
      setRequests([]);
    }
  }, []);

  const load = useCallback(
    async ({ silent = false, keepOnEmpty = false } = {}) => {
      if (!orgId) return null;
      const fetchId = ++fetchIdRef.current;
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setError("");
      }
      try {
        const data = await fetchQueueData();
        applyQueueResult(fetchId, data, { keepOnEmpty });
        if (fetchId === fetchIdRef.current) {
          await syncServerQueueCount();
        }
        return data;
      } catch (e) {
        if (fetchId === fetchIdRef.current && !silent) {
          setError(e.message || "Failed to load");
        }
        return null;
      } finally {
        if (fetchId === fetchIdRef.current) {
          if (silent) setRefreshing(false);
          else setLoading(false);
        }
      }
    },
    [orgId, fetchQueueData, applyQueueResult, syncServerQueueCount]
  );

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    load();
  }, [load, filterEmp, statusFilter]);

  const refreshAfterAction = useCallback(
    async (actedRequestId) => {
      skipNextAutoLoadRef.current = true;
      ++fetchIdRef.current;
      setRefreshing(true);

      let optimisticRemaining = [];
      setRequests((prev) => {
        optimisticRemaining = prev.filter((r) => r.request_id !== actedRequestId);
        return optimisticRemaining;
      });

      try {
        let data = await fetchQueueData();
        if (!Array.isArray(data) || data.length === 0) {
          await wait(350);
          data = await fetchQueueData();
        }
        if (Array.isArray(data) && data.length > 0) {
          setRequests(data);
        } else {
          const pendingCount = orgId ? await fetchActionableLeaveCount(orgId) : 0;
          if (pendingCount === 0) {
            setRequests([]);
          } else if (optimisticRemaining.length > 0) {
            setRequests(optimisticRemaining);
            await wait(500);
            const retry = await fetchQueueData();
            if (Array.isArray(retry) && retry.length > 0) {
              setRequests(retry);
            }
          }
        }
        notifyLeaveQueueUpdated();
      } catch (e) {
        if (optimisticRemaining.length > 0) setRequests(optimisticRemaining);
        setError(e.message || "Action saved, but the list could not be refreshed. Click Refresh.");
      } finally {
        setRefreshing(false);
      }
    },
    [fetchQueueData, orgId]
  );

  const executeAction = async (req, decision) => {
    const employee =
      formatEmployeeLabel(findEmployeeById(employees, req.employee_id)) || "this employee";

    setActingId(req.request_id);
    setError("");
    setSuccess("");
    setDrawerReq(null);
    setConfirmReq(null);
    try {
      const result = await approveLeaveRequest(req.request_id, { decision });
      const finalStatus = result?.status || decision;
      if (finalStatus === "approved") {
        setSuccess(`Leave approved for ${employee}. Attendance and balance have been updated.`);
      } else if (finalStatus === "escalated") {
        setSuccess(`Approved for ${employee}. Request sent to HR for final approval.`);
      } else if (finalStatus === "rejected") {
        setSuccess(`Leave rejected for ${employee}.`);
      } else {
        setSuccess(`Leave request updated for ${employee}.`);
      }
      await refreshAfterAction(req.request_id);
    } catch (e) {
      setError(e?.cause || e);
    } finally {
      setActingId(null);
    }
  };

  const handleApprove = (req) => executeAction(req, "approved");
  const handleReject = (req) => setConfirmReq(req);
  const confirmReject = () => {
    if (confirmReq) executeAction(confirmReq, "rejected");
  };

  /* KPI derived values */
  const kpiPending = useMemo(() => requests.filter((r) => r.status === "pending").length, [requests]);
  const kpiApproved = useMemo(() => requests.filter((r) => r.status === "approved").length, [requests]);
  const kpiRejected = useMemo(() => requests.filter((r) => r.status === "rejected").length, [requests]);
  const kpiActionable = useMemo(() => requests.filter((r) => r.can_act).length, [requests]);

  if (orgLoading) return <BrixigoPageLoader compact message="Loading…" />;

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="att-mvp-kpi-grid">
        <KpiCard icon={Clock} label="Pending" value={kpiPending} color="#f59e0b" accent="#fb923c" />
        <KpiCard icon={CheckCircle2} label="Approved" value={kpiApproved} color="#10b981" accent="#06b6d4" />
        <KpiCard icon={XCircle} label="Rejected" value={kpiRejected} color="#ef4444" accent="#f472b6" />
        <KpiCard icon={AlertTriangle} label="Need My Action" value={kpiActionable} color="#2563eb" accent="#7c3aed" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          {!queueOnly && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-[12px] font-semibold text-slate-600 mb-1 block">Employee</label>
              <EmployeeSelect
                value={filterEmp}
                onChange={(nextId) => setFilterEmp(isValidUuid(nextId) ? nextId : "")}
                allowEmpty
                emptyLabel="All employees"
                selectClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
              />
            </div>
          )}
          <div>
            <label className="text-[12px] font-semibold text-slate-600 mb-1 block">View</label>
            <HrSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">My queue (action required)</option>
              <option value="pending">Awaiting manager (all)</option>
              <option value="escalated">Awaiting HR (all)</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </HrSelect>
          </div>
          {!queueOnly && filterEmp && (
            <HrBtn type="button" variant="secondary" onClick={() => setFilterEmp("")}>
              Clear employee
            </HrBtn>
          )}
          <HrBtn onClick={() => load()} variant="primary" disabled={loading || refreshing}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading || refreshing ? "animate-spin" : ""}`} />
            Refresh
          </HrBtn>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <GuidedNotice domain="leave" error={error} variant="error" onDismiss={() => setError("")} />
      )}
      {success && <HrAlert variant="success">{success}</HrAlert>}
      {queueOnly && serverQueueCount > 0 && requests.length === 0 && !loading && !refreshing && (
        <HrAlert variant="info">
          You have {serverQueueCount} leave request{serverQueueCount === 1 ? "" : "s"} awaiting your approval.{" "}
          <button type="button" className="underline font-semibold" onClick={() => load()}>
            Reload queue
          </button>
        </HrAlert>
      )}

      {/* Request cards */}
      {loading ? (
        <BrixigoPageLoader compact message="Loading requests…" />
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const empLabel = formatEmployeeLabel(findEmployeeById(employees, req.employee_id)) || req.employee_name || req.employee_code || "—";
            return (
              <div
                key={req.request_id}
                onClick={() => setDrawerReq(req)}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[12px] font-bold shadow-sm shrink-0">
                      {empLabel.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[14px] font-bold text-slate-900">{empLabel}</p>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold border ${STATUS_STYLES[req.status] || "bg-slate-100"}`}>
                          {statusLabel(req)}
                        </span>
                      </div>
                      <p className="text-[13px] text-slate-600 mt-1 flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{req.leave_type_name || req.leave_type_code}</span>
                        <span className="text-slate-300">·</span>
                        <span>{formatIndianDate(req.start_date)} → {formatIndianDate(req.end_date)}</span>
                        {req.half_day && (
                          <span className="text-[10px] bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded-full font-bold">Half-day</span>
                        )}
                      </p>
                      {req.reason && (
                        <p className="text-[12px] text-slate-500 mt-1 italic line-clamp-1">"{req.reason}"</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[22px] font-extrabold text-blue-600 tabular-nums leading-none">{req.total_days}</p>
                    <p className="text-[10px] text-slate-400 font-bold">day{req.total_days !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                {req.can_act && ["pending", "escalated"].includes(req.status) && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                    <button
                      type="button"
                      disabled={actingId === req.request_id}
                      onClick={(e) => { e.stopPropagation(); handleApprove(req); }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm disabled:opacity-50 transition"
                    >
                      <CheckCircle2 size={13} />
                      {approveButtonLabel(req, normalizedRole)}
                    </button>
                    <button
                      type="button"
                      disabled={actingId === req.request_id}
                      onClick={(e) => { e.stopPropagation(); handleReject(req); }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 disabled:opacity-50 transition"
                    >
                      <XCircle size={13} />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {!loading && !refreshing && requests.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
              <HrEmptyState
                icon={ClipboardList}
                title={queueOnly ? "No leave awaiting your approval" : "No leave requests"}
                description={
                  queueOnly
                    ? serverQueueCount > 0
                      ? "Requests are assigned to you — try Reload queue above."
                      : "You will only see requests when it is your turn in the approval chain."
                    : "Try changing filters or check back when employees apply for leave."
                }
              />
              {!queueOnly && (filterEmp || statusFilter) && (
                <div className="mt-4 text-center">
                  <HrBtn type="button" onClick={clearBrowseFilters}>Back to my queue</HrBtn>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Detail drawer */}
      <ApprovalDrawer
        req={drawerReq}
        employees={employees}
        role={role}
        acting={actingId === drawerReq?.request_id}
        onApprove={handleApprove}
        onReject={handleReject}
        onClose={() => setDrawerReq(null)}
      />

      {/* Reject confirmation */}
      <ConfirmDialog
        open={!!confirmReq}
        title="Reject leave request?"
        message={
          confirmReq
            ? `Reject ${confirmReq.leave_type_name || confirmReq.leave_type_code} for ${
                formatEmployeeLabel(findEmployeeById(employees, confirmReq.employee_id)) || "this employee"
              } (${formatIndianDate(confirmReq.start_date)} – ${formatIndianDate(confirmReq.end_date)})?`
            : ""
        }
        confirmLabel="Reject"
        danger
        loading={actingId === confirmReq?.request_id}
        onConfirm={confirmReject}
        onCancel={() => setConfirmReq(null)}
      />
    </div>
  );
}
